import { useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Map, InfoWindow, AdvancedMarker } from '@vis.gl/react-google-maps';

import { verticalMapId } from '@/constants/index';
import { scrollToTop } from '@/utils/scrollToTop';

import styles from './Map.module.scss';

const defaultLocation = {
  lat: 40.3763,
  lng: 49.81567,
};

const Maps = ({
  className,
  markers = [],
  disableMarkerClick = false,
  customerLocation,
}) => {
  const { t } = useTranslation();
  const [center, setCenter] = useState(customerLocation || defaultLocation);
  const [activeMarker, setActiveMarker] = useState(null);
  const [zoom, setZoom] = useState(9);
  const [userDragging, setUserDragging] = useState(false);

  const onMapDragStart = () => {
    setUserDragging(true);
  };

  const onMapDrag = (event) => {
    const newCenter = event.map.getCenter().toJSON();
    setCenter(newCenter);
    setUserDragging(false);
  };

  useEffect(() => {
    if (customerLocation) {
      scrollToTop();
      setCenter({ lat: +customerLocation.lat, lng: +customerLocation.lng });
      setZoom(15);
    } else if (markers.length > 0) {
      setCenter({ lat: +markers[0].lat, lng: +markers[0].lng });
      setZoom(9);
    }
    setActiveMarker(null);
  }, [customerLocation, markers]);

  const renderMarkers = (item, index) => {
    return (
      <AdvancedMarker
        key={index}
        position={{ lat: Number(item.lat), lng: Number(item.lng) }}
        onClick={() => {
          if (!disableMarkerClick) setActiveMarker(index);
        }}
        title={item.name}
      >
        {activeMarker === index && (
          <InfoWindow
            position={{ lat: Number(item.lat), lng: Number(item.lng) }}
            onCloseClick={() => setActiveMarker(null)}
          >
            <div className={styles.infoWindow}>
              {!!item?.name && (
                <div className={styles.infoWindowTitle}>{item?.name}</div>
              )}
              {!!item?.description && (
                <div className={styles.infoWindowAddress}>
                  {item?.description}
                </div>
              )}
              {item?.working_hours?.length > 0 && (
                <div className={styles.infoWindowBlock}>
                  <div className={styles.label}>{t('common:workingHours')}</div>
                  <div className={styles.value}>
                    {item?.working_hours?.map((item) => (
                      <div
                        className={`${styles.hours} grid-extend`}
                        key={item?.id}
                      >
                        <span>{item?.days}:</span>
                        <span>{item?.working_hours}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {item?.email && (
                <div className={styles.infoWindowBlock}>
                  <div className={styles.label}>{t('common:email')}</div>
                  <div className={`${styles.value} grid-extend`}>
                    <div>{item?.email}</div>
                  </div>
                </div>
              )}
              {item?.phone_number && (
                <div className={styles.infoWindowBlock}>
                  <div className={styles.label}>{t('common:phone')}</div>
                  <div className={`${styles.value} grid-extend`}>
                    <div>{item?.phone_number}</div>
                  </div>
                </div>
              )}
            </div>
          </InfoWindow>
        )}
        <img
          src='/assets/map/AddressMarker.svg'
          alt={item?.name}
          title={item?.name}
          width={50}
          height={50}
        />
      </AdvancedMarker>
    );
  };

  return (
    <div className={`${className} ${styles.map}`}>
      <Map
        id='contact-us'
        defaultCenter={center}
        defaultZoom={zoom}
        center={!userDragging ? center : null}
        onDragstart={onMapDragStart}
        onDragend={onMapDrag}
        onClick={() => setActiveMarker(null)}
        mapId={verticalMapId}
        options={{
          disableDefaultUI: true,
          gestureHandling: 'greedy',
          draggable: true,
          zoomControl: true,
        }}
      >
        {markers.map(renderMarkers)}
      </Map>
    </div>
  );
};

export default Maps;
